import { Badge, Button, Text } from "@mantine/core";
import { IconPlus } from "@tabler/icons-react";
import type { ColumnDef } from "@tanstack/react-table";
import { useState } from "react";
import { DataTable, PageHeader } from "@how/ui";
import { useCampusOptions } from "../groups/group-queries";
import { CheckInTabs } from "./CheckInTabs";
import { DoorModal } from "./DoorModal";
import { useManagedDoors, type ManagedDoor } from "./door-queries";

const PAGE = { pageIndex: 0, pageSize: 200 };

/**
 * The church's check-in doors (ADR 0078): where each station stands, named by the church, at a campus or any. The
 * station asks which door it is at. Reached by those who manage events, the permission the database asks for; a row
 * opens the dialog that renames, moves, archives or restores a door.
 */
export function DoorsPage() {
  const doors = useManagedDoors();
  const campuses = useCampusOptions();
  // undefined: closed. null: adding. A row: editing that door.
  const [open, setOpen] = useState<ManagedDoor | null | undefined>(undefined);
  const status = doors.isPending ? "loading" : doors.isError ? "error" : "ready";
  const multiCampus = (campuses.data?.length ?? 0) > 1;

  const columns: ColumnDef<ManagedDoor>[] = [
    {
      accessorKey: "name",
      header: "Door",
      enableSorting: false,
      cell: (ctx) => (
        <Text size="sm" fw={600}>
          {ctx.row.original.name}
        </Text>
      ),
    },
    ...(multiCampus
      ? [
          {
            accessorKey: "campusName",
            header: "Campus",
            enableSorting: false,
            cell: (ctx) =>
              ctx.row.original.campusName ?? (
                <Text size="sm" c="dimmed">
                  Any campus
                </Text>
              ),
          } satisfies ColumnDef<ManagedDoor>,
        ]
      : []),
    {
      accessorKey: "sortOrder",
      header: "Order",
      enableSorting: false,
      meta: { numeric: true },
      cell: (ctx) => ctx.row.original.sortOrder,
    },
    {
      accessorKey: "isActive",
      header: "Status",
      enableSorting: false,
      cell: (ctx) =>
        ctx.row.original.isActive ? (
          <Badge variant="light" color="teal">
            Active
          </Badge>
        ) : (
          <Badge variant="light" color="gray">
            Archived
          </Badge>
        ),
    },
  ];

  return (
    <>
      <PageHeader
        title="Check-in"
        description="The doors where your stations stand. A station asks which door it is at."
        actions={
          <Button
            leftSection={<IconPlus size={16} />}
            onClick={() => {
              setOpen(null);
            }}
          >
            Add door
          </Button>
        }
      />
      <CheckInTabs active="doors" />
      <DataTable
        label="Check-in doors"
        columns={columns}
        rows={doors.data ?? []}
        totalRows={doors.data?.length ?? 0}
        status={status}
        pagination={PAGE}
        onPaginationChange={() => undefined}
        sorting={[]}
        onSortingChange={() => undefined}
        getRowId={(row) => row.id}
        onRowClick={(row) => {
          setOpen(row);
        }}
        emptyTitle="No doors yet"
        emptyDescription="Add the doors your stations stand at, such as Main entrance or Kids wing, so each station can say where it is."
        errorMessage="Could not load the doors."
        onRetry={() => {
          void doors.refetch();
        }}
      />
      <DoorModal
        key={open === undefined ? "closed" : open === null ? "new" : open.id}
        door={open}
        otherNames={(doors.data ?? []).filter((d) => d.id !== open?.id).map((d) => d.name)}
        campuses={campuses.data ?? []}
        onClose={() => {
          setOpen(undefined);
        }}
      />
    </>
  );
}
